import { useState, useEffect } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { supabase } from '../lib/supabase';

const TUTORIAL_STORAGE_KEY = '@tutorial_completed';

/**
 * Hook to manage tutorial display state
 * Tracks tutorial completion per user to ensure it's shown only once per user
 */
export function useTutorial() {
  const [hasSeenTutorial, setHasSeenTutorial] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkTutorialStatus = async () => {
      try {
        // Get current user session
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session?.user?.id) {
          // No user logged in, don't show tutorial
          setHasSeenTutorial(true);
          setIsChecking(false);
          return;
        }

        // Check if this specific user has seen the tutorial
        // We use user ID to ensure each user sees it once, even on the same device
        const storageKey = `${TUTORIAL_STORAGE_KEY}_${session.user.id}`;
        console.log('[useTutorial] Checking tutorial status for user:', session.user.id);
        console.log('[useTutorial] Storage key:', storageKey);
        const tutorialStatus = await AsyncStorage.getItem(storageKey);
        console.log('[useTutorial] Tutorial status from storage:', tutorialStatus);
        
        // If the key exists and is 'true', user has seen the tutorial
        const hasSeen = tutorialStatus === 'true';
        
        setHasSeenTutorial(hasSeen);
        setIsChecking(false);
      } catch (error) {
        console.error('[useTutorial] Error checking tutorial status:', error);
        // On error, assume tutorial has been seen to avoid blocking the app
        setHasSeenTutorial(true);
        setIsChecking(false);
      }
    };

    checkTutorialStatus();

    // Also listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session?.user?.id) {
        // New user signed in, check their tutorial status
        console.log('[useTutorial] SIGNED_IN event detected, rechecking tutorial status');
        
        // Re-run the tutorial status check for the new user
        const storageKey = `${TUTORIAL_STORAGE_KEY}_${session.user.id}`;
        console.log('[useTutorial] Checking tutorial status for user:', session.user.id);
        
        try {
          const tutorialStatus = await AsyncStorage.getItem(storageKey);
          console.log('[useTutorial] Tutorial status from storage:', tutorialStatus);
          
          const hasSeen = tutorialStatus === 'true';
          setHasSeenTutorial(hasSeen);
          setIsChecking(false);
        } catch (error) {
          console.error('[useTutorial] Error checking tutorial status on sign-in:', error);
          // On error, assume tutorial has been seen to avoid blocking the app
          setHasSeenTutorial(true);
          setIsChecking(false);
        }
      } else if (event === 'SIGNED_OUT') {
        // User signed out, reset state
        setHasSeenTutorial(null);
        setIsChecking(true);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  /**
   * Mark tutorial as completed for the current user
   */
  const markTutorialComplete = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.user?.id) {
        console.warn('[useTutorial] No user session, cannot mark tutorial complete');
        return;
      }

      // Store completion status per user
      const storageKey = `${TUTORIAL_STORAGE_KEY}_${session.user.id}`;
        console.log('[useTutorial] Checking tutorial status for user:', session.user.id);
        console.log('[useTutorial] Storage key:', storageKey);
      await AsyncStorage.setItem(storageKey, 'true');
      console.log('[useTutorial] Saved to storage with key:', storageKey);
      
      setHasSeenTutorial(true);
      console.log('[useTutorial] Tutorial marked as complete for user:', session.user.id);
    } catch (error) {
      console.error('[useTutorial] Error marking tutorial complete:', error);
    }
  };

  /**
   * Reset tutorial status (useful for testing or admin purposes)
   * This will make the tutorial show again for the current user
   */
  const resetTutorial = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.user?.id) {
        console.warn('[useTutorial] No user session, cannot reset tutorial');
        return;
      }

      const storageKey = `${TUTORIAL_STORAGE_KEY}_${session.user.id}`;
        console.log('[useTutorial] Checking tutorial status for user:', session.user.id);
        console.log('[useTutorial] Storage key:', storageKey);
      await AsyncStorage.removeItem(storageKey);
      
      setHasSeenTutorial(false);
      console.log('[useTutorial] Tutorial reset for user:', session.user.id);
    } catch (error) {
      console.error('[useTutorial] Error resetting tutorial:', error);
    }
  };

  return {
    hasSeenTutorial,
    isChecking,
    markTutorialComplete,
    resetTutorial,
    shouldShowTutorial: hasSeenTutorial === false && !isChecking,
  };
}
